// ─────────────────────────────────────────────────────────────────────────────
// dottedMap.js — the offline dot-matrix world map behind the Map View.
//   `dotted-map` draws the continents as a grid of SVG dots: no tiles, no
//   network, no API key. Generating that grid is expensive and the result never
//   changes, so it is built once (lazily, on the first Map View open) and shared
//   by every mount.
//
//   projectNodes() converts lat/lng into percentages of the *same* SVG box, which
//   is what lets plain HTML markers sit exactly on top of the dots. Nothing else
//   in the map knows about geography — this module owns the projection.
// ─────────────────────────────────────────────────────────────────────────────
import DottedMap from "dotted-map";
import { statusColor } from "../badges";

// height = dot rows; the library derives the column count from the projection.
const MAP_OPTIONS = { height: 60, grid: "diagonal" };
const DOT_STYLE = {
  radius: 0.22,
  color: "#33414d",
  shape: "circle",
  backgroundColor: "transparent",
};

// Used only if generation throws, so the panel keeps its shape instead of
// collapsing to zero height. Matches the viewBox MAP_OPTIONS produces.
const FALLBACK_SIZE = { width: 119, height: 60 };

let instance = null;
let baseMap = null;

function getInstance() {
  if (!instance) instance = new DottedMap(MAP_OPTIONS);
  return instance;
}

/**
 * The shared base map, generated on first call and cached from then on.
 *
 * @returns {{ svg: string|null, width: number, height: number }}
 *   `svg` is the raw markup to inject; it is null when dotted-map failed, in
 *   which case callers show a fallback. width/height are the SVG's viewBox size
 *   — the aspect ratio the map canvas must be locked to (see index.js).
 */
export function getBaseMap() {
  if (!baseMap) {
    try {
      const map = getInstance();
      baseMap = {
        svg: map.getSVG(DOT_STYLE),
        width: map.image.width,
        height: map.image.height,
      };
    } catch (err) {
      console.warn("[NodeMap] dotted-map failed:", err?.message);
      baseMap = { svg: null, ...FALLBACK_SIZE };
    }
  }
  return baseMap;
}

/**
 * Places nodes on the base map. Nodes without usable coordinates are dropped —
 * there is no honest position to draw them at.
 *
 * @param {object[]} nodes  enriched nodes (need latitude/longitude/status)
 * @returns {{ node: object, xPct: number, yPct: number, color: string }[]}
 *   xPct/yPct are percentages of the map box, ready for CSS left/top.
 */
export function projectNodes(nodes) {
  const { svg, width, height } = getBaseMap();
  if (!svg) return [];

  const map = getInstance();
  return nodes
    .filter((n) => Number.isFinite(n.latitude) && Number.isFinite(n.longitude))
    .map((n) => {
      const { x, y } = map.getPin({ lat: n.latitude, lng: n.longitude });
      return {
        node: n,
        xPct: (x / width) * 100,
        yPct: (y / height) * 100,
        color: statusColor(n.status),
      };
    });
}
